<template>
	<div class="key-result-form">
		<KeyResultFormRowLayout>
			<template #left-column>
				<LabeledInputField
					:blur-is-input="false"
					:disabled="disabled || disabledFormFields.name"
					:focus="isFocused( `name` )"
					:is-valid="!errors.name"
					:selection="focusedSelectionFor( `name` )"
					:validation-error="errorFor( `name` )"
					:value="sanitizedName"
					@click="onNameFocus"
					@input="onNameInput"
					:hint="translate( `nameFieldHint` )"
					:label="translate( `nameFieldLabel` )"
					name="name"
					:placeholder="translate( `nameFieldPlaceholder` )"
				/>
			</template>
			<template #right-column>
				<LabeledConfidenceLevelDropDown
					:disabled="disabled || disabledFormFields.confidenceLevel"
					:is-valid="!errors.confidenceLevel"
					:validation-error="errorFor( `confidenceLevel` )"
					:value="confidenceLevel"
					@change="onConfidenceLevelChanged"
					:hint="translate( `confidenceLevelFieldHint` )"
					:label="translate( `confidenceLevelFieldLabel` )"
					name="confidenceLevel"
				/>
			</template>
		</KeyResultFormRowLayout>

		<KeyResultFormRowLayout>
			<template #left-column>
				<LabeledDropDown
					:disabled="disabled || disabledFormFields.type"
					:is-valid="!errors.type"
					:options="options.type"
					:validation-error="errorFor( `type` )"
					:value="type"
					:hint="translate( `typeFieldHint` )"
					:label="translate( `typeFieldLabel` )"
					name="type"
					@change="onTypeChanged"
				/>
			</template>
			<template #right-column>
				<div v-if="isBinary" class="split-field">
					<div>
						<LabeledDropDown
							:disabled="disabled || disabledFormFields.currentValue"
							:is-valid="!errors.currentValue"
							:options="options.binaryChoice"
							:validation-error="errorFor( `currentValue` )"
							:value="currentValue"
							:label="translate( `binaryFieldLabel` )"
							name="currentValue"
							@change="onCurrentValueChanged"
						/>
					</div>
				</div>

				<div v-if="isCurrency" class="split-field">
					<div>
						<LabeledInputField
							:blur-is-input="false"
							:disabled="disabled || disabledFormFields.currentValue"
							:focus="isFocused( `currentValue` )"
							:is-valid="!errors.currentValue"
							:left-icon="[ `far`, `dollar-sign`, ]"
							:selection="focusedSelectionFor( `currentValue` )"
							:validation-error="errorFor( `currentValue` )"
							:value="currentValue"
							:label="translate( `currentValueFieldLabel` )"
							name="currentValue"
							@click="onCurrentValueFocus"
							@input="onCurrentValueInput"
						/>
					</div>
					<div>
						<LabeledInputField
							:blur-is-input="false"
							:disabled="disabled || disabledFormFields.targetValue"
							:focus="isFocused( `targetValue` )"
							:is-valid="!errors.targetValue"
							:left-icon="[ `far`, `dollar-sign`, ]"
							:selection="focusedSelectionFor( `targetValue` )"
							:validation-error="errorFor( `targetValue` )"
							:value="targetValue"
							:label="translate( `targetValueFieldLabel` )"
							name="targetValue"
							@click="onTargetValueFocus"
							@input="onTargetValueInput"
						/>
					</div>
				</div>

				<div v-if="isNumeric" class="split-field">
					<div>
						<LabeledInputField
							:blur-is-input="false"
							:disabled="disabled || disabledFormFields.currentValue"
							:focus="isFocused( `currentValue` )"
							:is-valid="!errors.currentValue"
							:selection="focusedSelectionFor( `currentValue` )"
							:validation-error="errorFor( `currentValue` )"
							:value="currentValue"
							:label="translate( `currentValueFieldLabel` )"
							name="currentValue"
							@click="onCurrentValueFocus"
							@input="onCurrentValueInput"
						/>
					</div>
					<div>
						<LabeledInputField
							:blur-is-input="false"
							:disabled="disabled || disabledFormFields.targetValue"
							:focus="isFocused( `targetValue` )"
							:is-valid="!errors.targetValue"
							:selection="focusedSelectionFor( `targetValue` )"
							:validation-error="errorFor( `targetValue` )"
							:value="targetValue"
							:label="translate( `targetValueFieldLabel` )"
							name="targetValue"
							@click="onTargetValueFocus"
							@input="onTargetValueInput"
						/>
					</div>
				</div>

				<div v-if="isPercentage" class="split-field">
					<div>
						<LabeledInputField
							:blur-is-input="false"
							:disabled="disabled || disabledFormFields.currentValue"
							:focus="isFocused( `currentValue` )"
							:is-valid="!errors.currentValue"
							:right-icon="[ `far`, `percent`, ]"
							:selection="focusedSelectionFor( `currentValue` )"
							:validation-error="errorFor( `currentValue` )"
							:value="currentValue"
							:label="translate( `currentValueFieldLabel` )"
							name="currentValue"
							@click="onCurrentValueFocus"
							@input="onCurrentValueInput"
						/>
					</div>
					<div>
						<LabeledInputField
							:blur-is-input="false"
							:disabled="disabled || disabledFormFields.targetValue"
							:focus="isFocused( `targetValue` )"
							:is-valid="!errors.targetValue"
							:right-icon="[ `far`, `percent`, ]"
							:selection="focusedSelectionFor( `targetValue` )"
							:validation-error="errorFor( `targetValue` )"
							:value="targetValue"
							:label="translate( `targetValueFieldLabel` )"
							name="targetValue"
							@click="onTargetValueFocus"
							@input="onTargetValueInput"
						/>
					</div>
				</div>
			</template>
		</KeyResultFormRowLayout>

		<KeyResultFormRowLayout>
			<template #left-column>
				<LabeledInputField
					:blur-is-input="false"
					:disabled="true"
					:value="startDate"
					:label="translate( `startDateFieldLabel` )"
					name="startDate"
				/>
			</template>
			<template #right-column>
				<LabeledInputField
					:blur-is-input="false"
					:disabled="true"
					:value="endDate"
					:label="translate( `endDateFieldLabel` )"
					name="endDate"
				/>
			</template>
		</KeyResultFormRowLayout>

		<KeyResultFormRowLayout>
			<template #left-column>
				<LabeledUserDropDown
					:disabled="disabled || disabledFormFields.ownerId"
					:is-valid="!errors.ownerId"
					:user-list="possibleKeyResultOwners"
					:validation-error="errorFor( `ownerId` )"
					:value="ownerId"
					:hint="translate( `ownerIdFieldHint` )"
					:label="translate( `ownerIdFieldLabel` )"
					name="ownerId"
					@change="onOwnerIdChanged"
				/>
			</template>
			<template #right-column>
				<LabeledInputField
					:blur-is-input="false"
					:disabled="true"
					:value="parentGoalName"
					:label="translate( `parentGoalFieldLabel` )"
					name="parentGoalName"
				/>
			</template>
		</KeyResultFormRowLayout>

		<div v-if="!hiddenFormFields.comment" class="divider" />

		<div v-if="!hiddenFormFields.comment" class="key-result-form__comment-row">
			<LabeledTextareaField
				:disabled="disabled || disabledFormFields.comment"
				:focus="isFocused( `comment` )"
				:hint="translate( `commentFieldHint` )"
				input-class-name="goal-form__comment-input"
				:is-valid="!errors.comment"
				:selection="focusedSelectionFor( `comment` )"
				:validation-error="errorFor( `comment` )"
				:value="comment"
				name="comment"
				:placeholder="translate( `commentFieldPlaceholder` )"
				@input="onCommentInput"
				@click="onCommentFocus"
			>
				<template #label>
					<div class="key-result-form__comment-labels">
						<label>{{ translate( `commentFieldLabel` ) }}</label>
						<label>{{ translate( `commentFieldOptionalLabel` ) }}</label>
					</div>
				</template>
			</LabeledTextareaField>
		</div>
	</div>
</template>

<script>
import DOMPurify from "dompurify";

import { KeyResultBinaryChoice, KeyResultType } from "@teaming/common-enums";

import {
	FormMixin,
	LabeledInputField,
	LabeledConfidenceLevelDropDown,
	LabeledDropDown,
	LabeledUserDropDown,
	LabeledTextareaField
} from "@teaming/common-ui";

import KeyResultFormRowLayout from "./KeyResultFormRowLayout";

export default {
	name: "KeyResultForm",
	mixins: [ FormMixin, ],
	components: {
		KeyResultFormRowLayout,
		LabeledInputField,
		LabeledDropDown,
		LabeledConfidenceLevelDropDown,
		LabeledUserDropDown,
		LabeledTextareaField,
	},
	props: {
		name: {
			type: String,
			required: true,
		},
		confidenceLevel: {
			type: String,
			required: true,
		},
		type: {
			type: String,
			required: true,
			validator( value ) {
				return KeyResultType.includes( value );
			},
		},
		startValue: {
			type: String,
			required: true,
		},
		currentValue: {
			type: String,
			required: true,
		},
		targetValue: {
			type: String,
			required: true,
		},
		ownerId: {
			type: String,
			required: true,
		},
		possibleKeyResultOwners: {
			type: Array,
			required: true,
		},
		startDate: {
			type: String,
			required: true,
		},
		endDate: {
			type: String,
			required: true,
		},
		parentGoalName: {
			type: String,
			required: true,
		},
		comment: {
			type: String,
			default: "",
		},
	},
	data() {
		return {
			formNameEvent: null,
		};
	},
	computed: {
		/** see FormMixin **/
		// eslint-disable-next-line vue/no-unused-properties
		form() {
			const {
				formId,
				sanitizedName: name,
				confidenceLevel,
				type,
				startValue,
				currentValue,
				targetValue,
				ownerId,
			} = this;

			return {
				formId,
				name,
				confidenceLevel,
				type,
				startValue,
				currentValue,
				targetValue,
				ownerId,
			};
		},
		disabledFormFields() {
			return {
				name: false,
				confidenceLevel: false,
				type: false,
				currentValue: false,
				targetValue: false,
				ownerId: false,
				comment: false,
				/** see FormMixin **/
				...this.disabledFields,
			};
		},
		// eslint-disable-next-line vue/no-unused-properties
		hiddenFormFields() {
			return {
				name: false,
				confidenceLevel: false,
				type: false,
				currentValue: false,
				targetValue: false,
				ownerId: false,
				comment: false,
				/** see FormMixin **/
				...this.hiddenFields,
			};
		},
		options() {
			return {
				type: [
					{
						text: "Binary",
						value: KeyResultType.BINARY,
					},
					{
						text: "Percentage",
						value: KeyResultType.PERCENTAGE,
					},
					{
						text: "Currency",
						value: KeyResultType.CURRENCY,
					},
					{
						text: "Numeric",
						value: KeyResultType.NUMERIC,
					},
				],
				binaryChoice: [
					{
						text: "Incomplete",
						value: KeyResultBinaryChoice.INCOMPLETE,
					},
					{
						text: "Complete",
						value: KeyResultBinaryChoice.COMPLETE,
					},
				],
			};
		},
		sanitizedName() {
			// Sample KRs may contain anchors, which we don't
			// want to display in the form field. If the user
			// edits the name in the form, the anchors will just
			// be lost. Oh well. They were just sample KRs anyway.
			return DOMPurify.sanitize( this.name, {
				ALLOWED_TAGS: [],
				ALLOWED_ATTR: [],
			} );
		},
		isBinary() {
			return this.type === KeyResultType.BINARY;
		},
		isPercentage() {
			return this.type === KeyResultType.PERCENTAGE;
		},
		isCurrency() {
			return this.type === KeyResultType.CURRENCY;
		},
		isNumeric() {
			return this.type === KeyResultType.NUMERIC;
		},
	},
	methods: {
		translate( key, args = {} ) {
			return this.$t( `common-gokr.components.KeyResultForm.${ key }`, args );
		},
		onNameInput( e ) {
			clearTimeout( this.formNameEvent );
			this.formNameEvent = setTimeout( () => {
				const name = e.target.value;
				this.raiseFormChange( {
					name,
				} );
				const { selectionStart, } = e.target;
				this.raiseFocusChange( "name", selectionStart );
				// eslint-disable-next-line no-magic-numbers
			}, 50 );
		},
		onNameFocus( e ) {
			const { selectionStart, } = e.target;
			this.raiseFocusChange( "name", selectionStart );
		},
		onConfidenceLevelChanged( option ) {
			const { value: confidenceLevel, } = option;
			this.raiseFormChange( {
				confidenceLevel,
			} );
			this.raiseFocusChange( "confidenceLevel" );
		},
		onTypeChanged( option ) {
			const { value: type, } = option;
			this.raiseFormChange( {
				type,
			} );
			this.raiseFocusChange( "type" );
		},
		onOwnerIdChanged( member ) {
			const { personId: ownerId, } = member;
			this.raiseFormChange( {
				ownerId,
			} );
			this.raiseFocusChange( "ownerId" );
		},
		onCurrentValueChanged( option ) {
			const { value: currentValue, } = option;
			this.raiseFormChange( {
				currentValue,
			} );
			this.raiseFocusChange( "currentValue" );
		},
		onCurrentValueInput( e ) {
			const { value: currentValue, } = e.target;
			this.raiseFormChange( {
				currentValue,
			} );
			const { selectionStart, } = e.target;
			this.raiseFocusChange( "currentValue", selectionStart );
		},
		onCurrentValueFocus( e ) {
			const { selectionStart, } = e.target;
			this.raiseFocusChange( "currentValue", selectionStart );
		},
		onTargetValueInput( e ) {
			const { value: targetValue, } = e.target;
			this.raiseFormChange( {
				targetValue,
			} );
			const { selectionStart, } = e.target;
			this.raiseFocusChange( "targetValue", selectionStart );
		},
		onTargetValueFocus( e ) {
			const { selectionStart, } = e.target;
			this.raiseFocusChange( "targetValue", selectionStart );
		},
		onCommentInput( e ) {
			clearTimeout( this.handleChange );
			this.handleChange = setTimeout( () => {
				const comment = e.target.value;
				this.raiseFormChange( {
					comment,
				} );
				const { selectionStart, } = e.target;
				this.raiseFocusChange( "comment", selectionStart );
				// eslint-disable-next-line no-magic-numbers
			}, 50 );
		},
		onCommentFocus( e ) {
			const { selectionStart, } = e.target;
			this.raiseFocusChange( "comment", selectionStart );
		},
	},
};
</script>

<style lang="scss" scoped>
$keyResultFormOptionalLabelColor: $color2-400;

.key-result-form {
	display: flex;
	flex-direction: column;
	justify-content: flex-start;
	gap: 1rem;

	// tiny padding that prevents the field outlines from being cut off
	padding: 0.07rem;
}

.split-field {
	display: flex;
	flex-direction: row;
	justify-content: space-evenly;
	gap: 1rem;

	> * {
		flex: 1;
	}
}

.key-result-form__actions {
	display: flex;
	flex-direction: row;
	justify-content: flex-start;
}

.key-result-form .divider {
	height: 0.0625rem;
	margin: 0.5rem 0;
	width: 100%;
	background-color: $borderColor--dark;
}

.key-result-form__comment-row {
	display: flex;
	flex-direction: column;
	justify-content: flex-start;
}

.key-result-form__comment-labels {
	display: flex;
	flex-direction: row;
	justify-content: space-between;

	:nth-child(2) {
		color: $keyResultFormOptionalLabelColor;
	}
}
</style>
